import { motion } from "framer-motion";
import { CreditCard, Landmark, ShieldCheck, TrendingUp, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const FINANCE_FEATURES = [
  {
    icon: CreditCard,
    title: "Instant Credit Lines",
    desc: "Get approved for trade credit up to ₹5 Cr within 48 hours. Digital-first underwriting powered by AI risk assessment.",
    stat: "₹5 Cr",
    statLabel: "Max Credit Line",
  },
  {
    icon: Landmark,
    title: "Supply Chain Finance",
    desc: "Early payment programs for suppliers. Extend your payables while your partners get paid faster.",
    stat: "48hrs",
    statLabel: "Approval Time",
  },
  {
    icon: ShieldCheck,
    title: "Trade Insurance",
    desc: "Protect your transactions with comprehensive trade credit insurance across all Indian states.",
    stat: "100%",
    statLabel: "Coverage",
  },
  {
    icon: TrendingUp,
    title: "Dynamic Pricing",
    desc: "Interest rates that reward your trade history. The more you trade, the better your terms.",
    stat: "2.5%",
    statLabel: "Starting APR",
  },
];

export function ByteCredit() {
  return (
    <section id="finance" data-testid="finance-section" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left column - text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">
              Byte-Credit
            </span>
            <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
              Financial Infrastructure for{" "}
              <span className="text-ib-blue">Metal Trade</span>
            </h2>
            <p className="font-manrope text-base md:text-lg text-ib-text-secondary mt-4 mb-8 leading-relaxed">
              SMEs in the metal sector deserve better financing. Byte-Credit
              provides instant credit, supply chain finance, and trade
              insurance — all integrated into the ironByte platform.
            </p>

            <div className="glass rounded-xl p-6 mb-6">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-jetbrains text-4xl font-bold text-ib-lime glow-lime-text">₹120 Cr+</span>
                <span className="font-manrope text-sm text-ib-text-secondary">credit facilitated</span>
              </div>
              <div className="w-full h-1.5 rounded-full bg-ib-steel/20 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "78%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, delay: 0.3 }}
                  className="h-full bg-gradient-to-r from-ib-blue to-ib-lime rounded-full"
                />
              </div>
              <p className="font-jetbrains text-[10px] text-ib-text-muted mt-2">78% utilization rate across active credit lines</p>
            </div>

            <Button
              data-testid="finance-learn-more-btn"
              className="bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit gap-2 rounded-full px-6"
            >
              Learn More About Byte-Credit
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>

          {/* Right column - feature cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {FINANCE_FEATURES.map((feat) => {
              const Icon = feat.icon;
              const featIdx = FINANCE_FEATURES.indexOf(feat);
              return (
                <motion.div
                  key={feat.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: featIdx * 0.1 }}
                  className="bento-card rounded-lg border border-ib-steel/20 bg-ib-surface/50 p-5"
                  data-testid={`finance-card-${featIdx}`}
                >
                  <div className="inline-flex p-2 rounded-lg bg-ib-blue/10 mb-3">
                    <Icon className="w-4 h-4 text-ib-blue" />
                  </div>
                  <h3 className="font-outfit font-bold text-sm text-ib-text mb-1.5">{feat.title}</h3>
                  <p className="font-manrope text-xs text-ib-text-secondary leading-relaxed mb-3">
                    {feat.desc}
                  </p>
                  <div className="border-t border-ib-steel/10 pt-3">
                    <span className="font-jetbrains text-lg font-bold text-ib-lime">{feat.stat}</span>
                    <span className="font-jetbrains text-[10px] text-ib-text-muted ml-2">{feat.statLabel}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
